<script setup lang="ts">
import { computed, onBeforeUnmount, reactive, ref, watch } from "vue";
import { formatNumber, formatNumberForScale } from "chartjs-graph";
import { PlanEditTarget, useBasePriceStore } from "@/modules/base_price/store";
import PlanPatternNudge from "@/modules/base_price/components/PlanPatternNudge.vue";
import ResolvedAssetDTO = Modules.BasePrice.Data.Resolved.ResolvedAssetDTO;

/**
 * How much of this target's inventory the selected reservation book takes,
 * month by month. Shaped like the Fill & CPM tab — twelve draggable bars over
 * twelve inputs — but with only one editable figure: the share. CPM here is
 * an outcome, not an input (budget ÷ what the reservation leaves it to sell),
 * so it is reported and never edited.
 */
const props = defineProps<{ target: PlanEditTarget }>();

const store = useBasePriceStore();

const MONTHS = [
	"Jan",
	"Feb",
	"Mar",
	"Apr",
	"May",
	"Jun",
	"Jul",
	"Aug",
	"Sep",
	"Oct",
	"Nov",
	"Dec",
];

const draft = reactive<number[]>(Array(12).fill(0));
const original = reactive<number[]>(Array(12).fill(0));
const dirty = ref(false);

const seedFrom = (values: number[]) => {
	values.forEach((value, i) => {
		draft[i] = Math.round(value * 10) / 10;
		original[i] = draft[i];
	});
	dirty.value = false;
};

/**
 * Seeds from the row that was applied, falling back to the resolved share
 * only where this target has none of its own — which is inheritance, and the
 * resolved value is exactly what it inherits (an ancestor's share, or the
 * book's own "Impressions reserved" when nothing overrides it).
 */
const seedFromDraft = () => {
	const own = store.targetOverride("impression_share");

	seedFrom(own?.monthly_shares ?? props.target.monthly_impression_share);
};

watch(
	() => [props.target.kind, props.target.id, store.selectedBookUuid],
	seedFromDraft,
	{ immediate: true }
);

/**
 * What every *other* enabled book already takes from this exact target, month
 * by month. Reservation books don't compete — each takes its share of the
 * full gross and only open inventory absorbs the difference — so the ceiling
 * on this book is whatever the others have left untaken.
 */
const takenByOthers = computed<number[]>(() => {
	const totals = Array(12).fill(0);

	store.reservationBooks
		.filter((book) => book.is_enabled && book.uuid !== store.selectedBookUuid)
		.forEach((book) => {
			const resolved = store.resolvedBook(book);
			if (!resolved) return;

			const nodes = resolved.nodes ? Object.values(resolved.nodes) : [];

			const shares =
				props.target.kind === "asset"
					? nodes
							.flatMap<ResolvedAssetDTO>((node) =>
								node.assets ? Object.values(node.assets) : []
							)
							.find((asset) => asset.asset_id === props.target.id)
							?.monthly_impression_share
					: nodes.find((node) => node.hierarchy_node_id === props.target.id)
							?.monthly_impression_share;

			shares?.forEach((share, i) => (totals[i] += share));
		});

	return totals;
});

/** The most this book may take in a month without the books together promising inventory twice. */
const ceilingFor = (i: number): number =>
	Math.max(0, 100 - takenByOthers.value[i]);

const setShare = (i: number, value: number) => {
	draft[i] = Math.min(ceilingFor(i), Math.max(0, value));
	dirty.value = true;
};

/** Reserved impressions behind the percentage — the quantity the share actually moves. */
const reservedFor = (i: number): number =>
	(props.target.monthly_gross_impressions[i] ?? 0) * (draft[i] / 100);

const atCeiling = (i: number) => draft[i] >= ceilingFor(i) - 0.05;

// --- Chart geometry ---
const chartHeight = 160;
const barHeight = (i: number) => (draft[i] / 100) * chartHeight;
const ceilingTop = (i: number) =>
	chartHeight - (ceilingFor(i) / 100) * chartHeight;

// --- Drag-to-edit ---
let dragIndex: number | null = null;
let dragStartY = 0;
let dragStartValue = 0;

const onDragMove = (event: PointerEvent) => {
	if (dragIndex === null) return;

	const delta = ((dragStartY - event.clientY) / chartHeight) * 100;
	setShare(dragIndex, dragStartValue + delta);
};

const onDragEnd = () => {
	dragIndex = null;
	window.removeEventListener("pointermove", onDragMove);
	window.removeEventListener("pointerup", onDragEnd);
};

const onBarPointerDown = (i: number, event: PointerEvent) => {
	dragIndex = i;
	dragStartY = event.clientY;
	dragStartValue = draft[i];
	window.addEventListener("pointermove", onDragMove);
	window.addEventListener("pointerup", onDragEnd);
};

onBeforeUnmount(() => {
	window.removeEventListener("pointermove", onDragMove);
	window.removeEventListener("pointerup", onDragEnd);
});

// --- Shift the whole pattern ---

/**
 * The slider beside the chart moves every month's share up or down by the
 * same amount, so the pattern keeps its shape. Every move works from the
 * months as they were when it was picked up, so dragging back to the middle
 * puts them back exactly; each month is capped like a typed share, at 0 and
 * at what the other books leave it (setShare()).
 */
let shiftSnapshot: { shares: number[]; dirty: boolean } | null = null;

const startPatternShift = () => {
	shiftSnapshot = { shares: draft.slice(), dirty: dirty.value };
};

const shiftPattern = (offset: number) => {
	if (!shiftSnapshot) startPatternShift();
	const snapshot = shiftSnapshot!;

	if (offset === 0) {
		snapshot.shares.forEach((share, i) => (draft[i] = share));
		dirty.value = snapshot.dirty;

		return;
	}

	snapshot.shares.forEach((share, i) =>
		setShare(i, Math.round((share + offset) * 10) / 10)
	);
};

const endPatternShift = () => {
	shiftSnapshot = null;
};

const shiftLabel = (offset: number) =>
	`${offset > 0 ? "+" : offset < 0 ? "−" : ""}${Math.abs(offset).toFixed(1)} pp`;

// --- Assists ---
const applyToEveryMonth = (value: number) => {
	MONTHS.forEach((_, i) => setShare(i, value));
};

const canReset = computed(
	() => props.target.impression_share_own && props.target.inheritsFrom !== null
);

const resetToInherited = () => {
	const inherited = props.target.inheritsFrom;
	if (!inherited) return;

	store.setTargetOverride("impression_share", null);
	seedFrom(inherited.monthly_impression_share);
};

// --- Totals ---
const reservedTotal = computed(() =>
	MONTHS.reduce((sum, _, i) => sum + reservedFor(i), 0)
);

const grossTotal = computed(() =>
	props.target.monthly_gross_impressions.reduce((sum, v) => sum + v, 0)
);

// --- Save / Cancel ---
const apply = () => {
	store.setTargetOverride("impression_share", {
		monthly_shares: draft.slice(),
	});

	original.forEach((_, i) => (original[i] = draft[i]));
	dirty.value = false;
};

const cancel = () => {
	original.forEach((value, i) => (draft[i] = value));
	dirty.value = false;
};
</script>

<template>
	<div>
		<div class="d-flex flex-wrap justify-space-between align-start ga-4 mb-3">
			<div class="text-body-2 text-medium-emphasis" style="max-width: 640px">
				How much of {{ target.name }}'s inventory
				{{ store.selectedBook?.name }} reserves, month by month. Whatever it
				doesn't take stays with open inventory. CPM follows from it — this
				book keeps its budget either way, so reserving more lowers its price
				and reserving less raises it.
			</div>

			<v-btn
				v-if="canReset"
				size="small"
				variant="tonal"
				color="error"
				prepend-icon="fa-light fa-arrow-rotate-left"
				@click="resetToInherited"
			>
				Reset to inherited
			</v-btn>
		</div>

		<div class="impshare-summary mb-4">
			<div class="d-flex ga-8 flex-wrap">
				<div>
					<div class="impshare-label">Reserved for this book</div>
					<div class="impshare-value">
						{{ formatNumberForScale("number_no_fraction", reservedTotal) }}
					</div>
				</div>
				<div>
					<div class="impshare-label">Of a gross</div>
					<div class="impshare-value">
						{{ formatNumberForScale("number_no_fraction", grossTotal) }}
					</div>
				</div>
				<div>
					<div class="impshare-label">Derived CPM</div>
					<div class="impshare-value">
						{{ formatNumber(target.cpm, "money") }}
					</div>
				</div>
			</div>
		</div>

		<div class="d-flex ga-4 flex-wrap mb-2 text-caption text-medium-emphasis">
			<span class="d-flex align-center ga-1">
				<span
					class="legend-square"
					style="background: rgb(var(--v-theme-info))"
				/>
				reserved by this book
			</span>
			<span class="d-flex align-center ga-1">
				<span class="legend-dash" />
				the most it may take — the rest is spoken for by other books
			</span>
		</div>

		<div class="d-flex">
		<div class="flex-grow-1" style="overflow-x: auto; min-width: 0">
			<div class="impshare-grid">
				<div
					v-for="(monthLabel, i) in MONTHS"
					:key="monthLabel"
					class="impshare-col"
				>
					<div
						class="impshare-bar-track"
						:style="{ height: chartHeight + 'px' }"
						@pointerdown="onBarPointerDown(i, $event)"
					>
						<div
							class="impshare-bar-fill"
							:style="{
								height: barHeight(i) + 'px',
								background: atCeiling(i)
									? 'rgb(var(--v-theme-warning))'
									: 'rgb(var(--v-theme-info))',
							}"
						/>
						<div
							v-if="ceilingFor(i) < 100"
							class="impshare-ceiling"
							:style="{ top: ceilingTop(i) + 'px' }"
							:title="`Other books already take ${(100 - ceilingFor(i)).toFixed(
								1
							)}% of this month`"
						/>
						<div class="impshare-bar-value">{{ draft[i].toFixed(1) }}%</div>
					</div>

					<div
						class="impshare-input-box"
						:class="{ 'impshare-input-box--at-ceiling': atCeiling(i) }"
					>
						<input
							type="number"
							step="1"
							min="0"
							:max="ceilingFor(i)"
							:value="Math.round(draft[i] * 10) / 10"
							@change="
								setShare(i, Number(($event.target as HTMLInputElement).value))
							"
						/>
						<span class="impshare-input-affix">%</span>
					</div>

					<div class="text-caption text-center text-medium-emphasis mt-1">
						{{ formatNumberForScale("number_no_fraction", reservedFor(i)) }}
					</div>

					<div class="text-caption text-center mt-1">{{ monthLabel }}</div>
				</div>
			</div>
		</div>

		<!-- Moves the whole pattern up or down together — see shiftPattern(). -->
		<plan-pattern-nudge
			:range="100"
			:step="0.1"
			color="info"
			:height="chartHeight"
			:format="shiftLabel"
			title="Move the whole impression share pattern up or down"
			:button-step="0.1"
			@start="startPatternShift"
			@shift="shiftPattern"
			@end="endPatternShift"
		/>
		</div>

		<div class="d-flex ga-2 flex-wrap align-center my-4">
			<v-btn size="small" variant="tonal" @click="applyToEveryMonth(draft[0])">
				Apply January to every month
			</v-btn>
		</div>

		<div class="d-flex justify-end ga-2">
			<v-btn variant="text" color="error" @click="cancel">Cancel</v-btn>
			<v-btn color="primary" :disabled="!dirty" @click="apply">
				Apply impression share
			</v-btn>
		</div>
	</div>
</template>

<style scoped lang="scss">
.impshare-summary {
	background: rgb(var(--v-theme-surface));
	border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
	border-radius: 10px;
	padding: 14px 20px;
}

.impshare-label {
	font-size: 12px;
	line-height: 16px;
	color: rgba(var(--v-theme-on-surface), 0.6);
	margin-bottom: 4px;
	white-space: nowrap;
}

.impshare-value {
	font-size: 14px;
	font-weight: 600;
	font-variant-numeric: tabular-nums;
}

.legend-square {
	width: 9px;
	height: 9px;
	border-radius: 2px;
	display: inline-block;
}

.legend-dash {
	width: 14px;
	height: 0;
	border-top: 1.5px dashed rgba(var(--v-theme-on-surface), 0.4);
	display: inline-block;
}

.impshare-grid {
	display: grid;
	grid-template-columns: repeat(12, minmax(64px, 1fr));
	gap: 6px;
	min-width: 860px;
}

.impshare-col {
	display: flex;
	flex-direction: column;
}

.impshare-bar-track {
	position: relative;
	border-radius: 8px;
	background: rgba(var(--v-theme-on-surface), 0.04);
	border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
	cursor: ns-resize;
	user-select: none;
	overflow: hidden;
}

.impshare-bar-fill {
	position: absolute;
	left: 3px;
	right: 3px;
	bottom: 0;
	border-radius: 4px 4px 0 0;
	transition: height 0.1s ease;
}

.impshare-ceiling {
	position: absolute;
	left: 0;
	right: 0;
	height: 0;
	border-top: 1.5px dashed rgba(var(--v-theme-on-surface), 0.4);
}

.impshare-bar-value {
	position: absolute;
	left: 0;
	right: 0;
	top: 6px;
	text-align: center;
	font-size: 11px;
	font-weight: 600;
	font-variant-numeric: tabular-nums;
}

.impshare-input-box {
	display: flex;
	align-items: center;
	justify-content: center;
	margin-top: 8px;
	height: 28px;
	border: 1.5px solid rgba(var(--v-theme-on-surface), 0.25);
	border-radius: 6px;
	background: rgb(var(--v-theme-surface));
}

.impshare-input-box--at-ceiling {
	border-color: rgb(var(--v-theme-warning));
}

.impshare-input-box input {
	width: 100%;
	min-width: 0;
	border: 0;
	outline: none;
	background: transparent;
	color: inherit;
	font: inherit;
	font-size: 12px;
	font-weight: 600;
	text-align: center;
	font-variant-numeric: tabular-nums;
}

.impshare-input-box input::-webkit-outer-spin-button,
.impshare-input-box input::-webkit-inner-spin-button {
	-webkit-appearance: none;
	margin: 0;
}

.impshare-input-affix {
	font-size: 11px;
	color: rgba(var(--v-theme-on-surface), 0.6);
	padding: 0 4px;
}
</style>
